/*
 * Customer Success (3.7b) in prototype mode: the sample in mock/success.js
 * (health, interventions, engagements, CS tasks, QBRs, NPS and CSAT, voice of
 * the customer, feature requests, expansion, renewals), mock/customers.js and
 * the in-product ticket ratings of mock/support.js, served in the shapes the
 * real API gives — HealthController, EngagementController, FeedbackController,
 * ExpansionController, RenewalController, SuccessController and Customer
 * 360's sections (Success360). Writes change the sample in memory until the
 * page is reloaded.
 *
 * Planned engagements and CS tasks carry ids "cst-N" and expansions "exp-N",
 * so the task and pipeline endpoints they share with Work and Sales are
 * answered here for them only (see the routes at the end).
 */
import { ok, created, fail, invalid, notFound, paginate, pageMeta, matches } from "./util";
import {
  health as sampleHealth, interventions as sampleInterventions, engagements as sampleEngagements, csTasks as sampleTasks, qbrs as sampleQbrs,
  csatResponses, npsResponses, npsTrend, vocRegister, featureRequests as sampleFeatureRequests, expansions as sampleExpansions, renewals as sampleRenewals,
} from "@/mock/success";
import { customers as sampleCustomers } from "@/mock/customers";
import { staff, products, packages, segments, taskTypes, priorities, pipelineStages, isSellable } from "@/mock/masters";
import { healthWeights, healthBands as settingBands, renewalOffsets, churnRiskRules } from "@/mock/settings";
import { ticketFeedback } from "@/mock/support";

// ---- Shared bits ------------------------------------------------------------------
const DAY = 86400000;
const now = () => new Date().toISOString();
const ago = (days) => new Date(Date.now() - days * DAY).toISOString();
const slug = (s) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
const sameId = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);
const ME_STAFF = 1; // Jane Wanjiku, the prototype's signed-in person
const startOfDay = (d) => new Date(new Date(d).toDateString()).getTime();
const daysTo = (d) => (d ? Math.round((startOfDay(d) - startOfDay(Date.now())) / DAY) : null);
const daysSince = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / DAY) : null);
const at10 = (day) => new Date(`${String(day).slice(0, 10)}T10:00:00`).toISOString();
const quarterStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
};
const quarterLabel = () => `Q${Math.floor(new Date().getMonth() / 3) + 1} ${new Date().getFullYear()}`;
const resourcePage = (items, q) => {
  const { data, ...p } = paginate(items, q, Number(q.per_page) || 50);
  return { data, links: { first: "?page=1", last: `?page=${p.last_page}`, prev: null, next: null }, meta: { current_page: p.current_page, from: p.from, last_page: p.last_page, per_page: p.per_page, to: p.to, total: p.total } };
};

const staffRow = (id) => {
  const s = staff.find((x) => sameId(x.id, id));
  return s ? { id: s.id, name: s.name } : null;
};
const customerOf = (id) => sampleCustomers.find((c) => sameId(c.id, id)) ?? null;
const healthOf = (id) => healthStore.find((h) => sameId(h.customer_id, id)) ?? null;
const customerRef = (id, extra = []) => {
  const c = customerOf(id);
  if (!c) return null;
  const row = { id: c.id, code: c.code, name: c.name, health: c.health };
  if (extra.includes("segment")) row.segment = String(c.segment).toLowerCase();
  if (extra.includes("health_score")) row.health_score = healthOf(c.id)?.score ?? null;
  if (extra.includes("lifecycle")) row.lifecycle = c.lifecycle;
  if (extra.includes("csm")) row.csm = staffRow(c.csm_id);
  return row;
};
const activeCustomers = () => sampleCustomers.filter((c) => c.lifecycle !== "former");
const productOf = (id) => products.find((p) => sameId(p.id, id)) ?? null;
const productRef = (id) => {
  const p = productOf(id);
  return p ? { id: p.id, code: p.code, name: p.short || p.name } : null;
};
const packageRef = (id) => {
  const p = packages.find((x) => sameId(x.id, id));
  return p ? { id: p.id, name: p.name } : null;
};

// ---- Settings → Customer Success Rules (mutable) -----------------------------------
const bandMin = Object.fromEntries(settingBands.map((b) => [b.key, b.min]));
const cfg = {
  health_weights: Object.fromEntries(healthWeights.map((w) => [w.key, w.weight])),
  health_bands: { healthy: bandMin.healthy, needs_attention: bandMin.needs_attention, at_risk: bandMin.at_risk },
  renewal_reminders: [...renewalOffsets],
  engagement_gap_days: 30,
  strategic_checkin_days: 30,
  qbr: { every_days: 90, segments: ["ent", "str", "mid"], auto_schedule: true },
  intervention: {
    on: true, bands: ["at_risk", "critical"], due_days: 2,
    playbook: "1. Call the decision maker within 48 hours.\n2. Review open tickets and complaints with Support.\n3. Agree a recovery plan with dates and send it in writing.\n4. Book a follow-up review in two weeks.",
  },
  lifecycle: { on: true, bands: ["critical"], renewal_at_risk: true },
  expansion: { licence_threshold: 85, healthy_only: true },
  survey_throttle_days: 30,
  churn_rules: churnRiskRules.map((r) => ({ key: r.key, trigger: r.trigger, condition: r.condition, value: r.value, unit: r.unit, raises: r.raises, on: r.on })),
  // Churn & Exit (3.8), as DefaultConfiguration::customerSuccess() starts them.
  churn_scoring: { weights: { health: 35, triggers: 30, renewal: 20, support: 15 }, bands: { medium: 30, high: 55, critical: 75 } },
  retention_playbooks: [
    { key: "recovery", name: "Service recovery", for: ["open_complaint", "poor_satisfaction"], owner: "csm", auto: true, steps: [
      { action: "Executive apology call", days: 2 }, { action: "Root-cause report to the customer", days: 5 },
      { action: "Weekly status call until stable", days: 7 }, { action: "Service credit if the SLA was breached (for approval)", days: 10, owner: "account_manager" },
    ] },
    { key: "adoption", name: "Re-adoption", for: ["usage_drop", "no_engagement"], owner: "csm", auto: true, steps: [
      { action: "Usage review with the customer admin", days: 3 }, { action: "Targeted retraining", days: 10 },
      { action: "Switch on unused modules", days: 14 }, { action: "30-day usage check", days: 30 },
    ] },
    { key: "commercial", name: "Commercial save", for: ["contract_expiry", "competitor_approach"], owner: "account_manager", auto: false, steps: [
      { action: "Value review (ROI) with the decision maker", days: 3 }, { action: "Right-size the package", days: 7 },
      { action: "Multi-year discount offer (for approval)", days: 10 }, { action: "Management call", days: 14, owner: "management" },
    ] },
    { key: "collections", name: "Payment recovery", for: ["late_payment"], owner: "finance", auto: true, steps: [
      { action: "Finance reminder", days: 1 }, { action: "Payment plan proposal", days: 7, owner: "csm" }, { action: "Service-hold warning (for approval)", days: 21 },
    ] },
  ],
  churn_management: { threshold: 600000, levels: ["high", "critical"], auto_escalate: true },
  exit_checklist: [
    { key: "payment", area: "Payment", item: "All invoices settled or written off", owner: "finance", required: true },
    { key: "data", area: "Data", item: "Data export delivered and receipt confirmed by the customer", owner: "csm", required: true },
    { key: "handback", area: "Data", item: "Data retention / deletion schedule agreed (DPA)", owner: "csm", required: true },
    { key: "users", area: "Users", item: "Customer users disabled", owner: "support", required: true },
    { key: "access", area: "Access", item: "Connected apps revoked, API tokens and SSO removed", owner: "support", required: true },
    { key: "contract", area: "Contract", item: "Termination letter issued; contract closed", owner: "account_manager", required: true },
    { key: "assets", area: "Assets", item: "Product access withdrawn: tenant suspended or standalone install handed back", owner: "implementation", required: true },
  ],
  win_back: { reengage_days: 180, data_deletion_days: 90 },
};
/** The rules as they stand, for the churn and exit stand-in (mock/api/retention.js). */
export const successRules = () => cfg;
const BANDS = { healthy: "Healthy", needs_attention: "Needs attention", at_risk: "At risk", critical: "Critical" };
const INDICATORS = Object.fromEntries(healthWeights.map((w) => [w.key, { name: w.label, source: w.source }]));
const reminders = () => [...new Set(cfg.renewal_reminders.map(Number).filter((d) => d > 0))].sort((a, b) => b - a);
const bandFor = (score) => (score >= cfg.health_bands.healthy ? "healthy" : score >= cfg.health_bands.needs_attention ? "needs_attention" : score >= cfg.health_bands.at_risk ? "at_risk" : "critical");

// ---- Lookups the screens pick from -----------------------------------------------
const option = (label, value = slug(label)) => ({ value, label });
const ENGAGEMENT_TYPES = [option("Call"), option("Meeting"), option("Visit"), option("Email"), option("Review"), option("Check-in"), option("QBR", "qbr"), option("Training")];
const TASK_TYPES = [...taskTypes.map((t) => option(t)), option("Check-in"), option("QBR", "qbr")];
const FEEDBACK_SOURCES = ["Visit", "QBR", "NPS comment", "Survey", "Complaint", "Check-in", "Ticket", "Call", "Email"].map((s) => option(s, s === "QBR" ? "qbr" : slug(s)));
const FEEDBACK_THEMES = ["Reliability", "Performance", "Billing", "Feature gap", "Usability", "Support", "Training", "Expansion", "Advocacy", "Pricing"].map((t) => option(t));
const labelOf = (list, v) => list.find((x) => x.value === v)?.label ?? (v ? v[0].toUpperCase() + v.slice(1).replace(/_/g, " ") : v);
const ENGAGEMENT_KEYS = ENGAGEMENT_TYPES.map((t) => t.value);

// ---- Health ------------------------------------------------------------------------
const healthStore = sampleHealth.map((h) => structuredClone(h));
const interventionStore = sampleInterventions.map((i) => ({
  id: `int-${i.id}`, customer_id: i.customer_id, rule: i.rule, trigger: null, action: i.action, owner_id: i.owner_id,
  status: slug(i.status), created: at10(i.created), task: null, from_band: null, to_band: null,
}));
const INTERVENTION_STATUSES = { open: "Open", in_progress: "In progress", done: "Done", cancelled: "Cancelled" };
const interventionRow = (i) => ({
  id: i.id, customer: customerRef(i.customer_id), rule: i.rule, trigger: i.trigger, action: i.action, owner: staffRow(i.owner_id), status: i.status,
  status_label: INTERVENTION_STATUSES[i.status] ?? i.status, task: i.task, created: i.created, from_band: i.from_band, to_band: i.to_band,
});
const interventionRows = () => [...interventionStore].sort((a, b) => Number(["done", "cancelled"].includes(a.status)) - Number(["done", "cancelled"].includes(b.status)) || b.created.localeCompare(a.created)).map(interventionRow);

const months = (n = 6) => Array.from({ length: n }, (_, i) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - (n - 1 - i));
  return { month: d.toISOString().slice(0, 7), label: d.toLocaleString("en", { month: "short" }) };
});
const weighted = (scores) => Math.round(Object.entries(cfg.health_weights).reduce((s, [k, w]) => s + ((scores[k] ?? 0) * w) / 100, 0));
const healthRow = (h) => {
  const c = customerOf(h.customer_id);
  return {
    customer: { id: c.id, code: c.code, name: c.name, segment: String(c.segment).toLowerCase(), industry: c.industry, lifecycle: c.lifecycle, csm: staffRow(c.csm_id), account_manager: staffRow(c.account_manager_id) },
    score: h.score, band: h.band, computed_at: h.computed_at ?? ago(1), scores: { ...h.scores }, trend: [...h.trend],
    licensed_users: h.licensed_users, active_users: h.active_users, inactive_users: h.inactive_users, adoption: h.adoption, arr: c.arr,
  };
};
const adoptionOf = (id) => {
  const h = healthOf(id);
  return {
    licensed_users: h?.licensed_users ?? null, active_users: h?.active_users ?? null, inactive_users: h?.inactive_users ?? null,
    adoption: h && h.licensed_users ? Math.min(100, Math.round((h.active_users / h.licensed_users) * 100)) : null,
    period: new Date().toISOString().slice(0, 8) + "01", logins: h ? h.active_users * 18 : null, features_used: null, modules_unused: h?.modules_unused ?? [],
  };
};
const recompute = (h) => {
  h.score = weighted(h.scores);
  h.band = bandFor(h.score);
  h.trend = [...h.trend.slice(0, 5), h.score];
  h.computed_at = now();
};

// Usage and payment signals: this month's usage for each customer, and two overdue invoices.
let signalSeq = 1;
const signalStore = [
  ...healthStore.flatMap((h) => [
    { id: `sig-${signalSeq++}`, customer_id: h.customer_id, type: "usage", metric: "active_users", value: h.active_users, period: months(1)[0].month + "-01", source: "manual", note: null },
    { id: `sig-${signalSeq++}`, customer_id: h.customer_id, type: "usage", metric: "licensed_users", value: h.licensed_users, period: months(1)[0].month + "-01", source: "manual", note: null },
  ]),
  { id: `sig-${signalSeq++}`, customer_id: 4, type: "payment", metric: "days_overdue", value: 48, period: months(1)[0].month + "-01", source: "manual", note: "Billing dispute" },
  { id: `sig-${signalSeq++}`, customer_id: 3, type: "payment", metric: "days_overdue", value: 21, period: months(1)[0].month + "-01", source: "manual", note: null },
];
const METRICS = {
  active_users: { type: "usage", label: "Active users (30 days)" }, licensed_users: { type: "usage", label: "Licensed users" }, logins: { type: "usage", label: "Logins in the period" },
  features_used: { type: "usage", label: "Features / modules in regular use" }, days_overdue: { type: "payment", label: "Days the oldest invoice is overdue" }, amount_overdue: { type: "payment", label: "Amount overdue" },
};
const signalRow = (s) => ({
  id: s.id, type: s.type, metric: s.metric, metric_label: METRICS[s.metric]?.label ?? s.metric, value: Number(s.value), period: s.period, source: s.source,
  details: null, note: s.note, product_account: null, product: null,
});

// ---- Engagements, planned engagements and CS tasks ---------------------------------
const isQbrTitle = (t) => /quarterly business review/i.test(t ?? "");
const heldStore = [];
const taskStore = [];
let heldSeq = 1;
let taskSeq = 1;
const addTask = (t) => {
  const n = taskSeq++;
  const row = { id: `cst-${n}`, code: `TSK-${String(900 + n).padStart(4, "0")}`, description: null, priority: "medium", status: "open", source: "manual", attendees: [], completed_at: null, ...t };
  taskStore.unshift(row);
  return row;
};
const addHeld = (e) => {
  const row = { id: `eng-${heldSeq++}`, summary: null, outcome: null, next_step: null, rag: null, attendees: [], task_id: null, ...e };
  heldStore.unshift(row);
  return row;
};
sampleEngagements.forEach((e) => {
  const type = isQbrTitle(e.title) ? "qbr" : slug(e.type);
  const attendees = e.attendees ? e.attendees.split(/;\s*/) : [];
  if (e.status === "Done") addHeld({ customer_id: e.customer_id, type, title: e.title, outcome: e.outcome, next_step: e.next_step, rag: type === "qbr" ? "green" : null, date: at10(e.date), owner_id: e.owner_id, attendees });
  else addTask({ customer_id: e.customer_id, type, title: e.title, due_at: at10(e.date), owner_id: e.owner_id, attendees });
});
sampleTasks.forEach((t) => addTask({
  customer_id: t.customer_id, type: slug(t.type), title: t.title, due_at: at10(t.due), owner_id: t.owner_id, priority: t.priority,
  status: t.status === "Done" ? "done" : "open", completed_at: t.status === "Done" ? at10(t.due) : null,
}));
// QBRs the sample shows as scheduled or missed are open QBR appointments.
sampleQbrs.filter((q) => ["Scheduled", "Missed"].includes(q.status) && !taskStore.some((t) => t.type === "qbr" && sameId(t.customer_id, q.customer_id) && t.status === "open"))
  .forEach((q) => addTask({ customer_id: q.customer_id, type: "qbr", title: `Quarterly business review — ${customerOf(q.customer_id)?.name}`, due_at: at10(q.date), owner_id: q.owner_id }));

const isOverdue = (t) => t.status === "open" && new Date(t.due_at).getTime() < Date.now();
const heldRow = (i) => ({
  id: i.id, kind: "interaction", status: "done", type: i.type, type_label: labelOf(ENGAGEMENT_TYPES, i.type), title: i.title, summary: i.summary, outcome: i.outcome,
  next_step: i.next_step, rag: i.rag, date: i.date, duration_minutes: null, attendees: [...i.attendees], customer: customerRef(i.customer_id, ["segment"]),
  owner: staffRow(i.owner_id), task_id: i.task_id,
});
const plannedRow = (t) => ({
  id: t.id, kind: "task", status: "planned", type: t.type, type_label: labelOf(TASK_TYPES, t.type), title: t.title, summary: t.description, outcome: null, next_step: null, rag: null,
  date: t.due_at, duration_minutes: null, attendees: [...t.attendees], customer: customerRef(t.customer_id, ["segment"]), owner: staffRow(t.owner_id),
  task_id: t.id, code: t.code, overdue: isOverdue(t),
});
const taskRow = (t) => ({
  id: t.id, code: t.code, type: t.type, type_label: labelOf(TASK_TYPES, t.type), title: t.title, description: t.description, priority: t.priority, due_at: t.due_at,
  status: isOverdue(t) ? "overdue" : t.status, source: t.source, owner: staffRow(t.owner_id), customer: customerRef(t.customer_id),
});
const searchHit = (search, title, customerId) => matches(search, title, customerOf(customerId)?.name);
const heldFor = (q = {}) => heldStore.filter((i) => (!q.customer_id || sameId(i.customer_id, q.customer_id)) && (!q.type || i.type === q.type)
  && (!q.owner_id || sameId(i.owner_id, q.owner_id)) && (!q.from || i.date >= new Date(q.from).toISOString()) && (!q.to || i.date.slice(0, 10) <= q.to)
  && searchHit(q.search, `${i.title} ${i.summary ?? ""}`, i.customer_id));
const plannedFor = (q = {}) => taskStore.filter((t) => t.status === "open" && ENGAGEMENT_KEYS.includes(t.type) && (!q.customer_id || sameId(t.customer_id, q.customer_id))
  && (!q.type || t.type === q.type) && (!q.owner_id || sameId(t.owner_id, q.owner_id)) && searchHit(q.search, t.title, t.customer_id));

const lastEngaged = (customers) => customers.map((c) => {
  const last = heldStore.filter((i) => sameId(i.customer_id, c.id) && i.date <= now()).sort((a, b) => b.date.localeCompare(a.date))[0] ?? null;
  return {
    customer: { id: c.id, code: c.code, name: c.name, health: c.health, segment: String(c.segment).toLowerCase(), lifecycle: c.lifecycle, csm: staffRow(c.csm_id) },
    last: last ? { type: last.type, type_label: labelOf(ENGAGEMENT_TYPES, last.type), title: last.title, at: last.date } : null,
    days: last ? daysSince(last.date) : null,
  };
});
const quiet = (days = cfg.engagement_gap_days) => lastEngaged(sampleCustomers.filter((c) => ["live", "at_risk"].includes(c.lifecycle)))
  .filter((r) => r.days === null || r.days >= days)
  .sort((a, b) => (b.days ?? Infinity) - (a.days ?? Infinity));

const engagementStats = () => {
  const by90 = heldFor({ from: ago(90) });
  return {
    last_30: heldFor({ from: ago(30) }).length,
    planned_14: plannedFor().filter((t) => new Date(t.due_at).getTime() <= Date.now() + 14 * DAY).length,
    overdue_tasks: taskStore.filter(isOverdue).length,
    quiet: quiet().length,
    gap_days: cfg.engagement_gap_days,
    by_type: ENGAGEMENT_TYPES.map((t) => ({ type: t.value, label: t.label, count: by90.filter((i) => i.type === t.value).length })),
  };
};

const qbrs = () => {
  const start = quarterStart().toISOString();
  return sampleCustomers.filter((c) => ["live", "at_risk"].includes(c.lifecycle) && cfg.qbr.segments.includes(String(c.segment).toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((c) => {
      const held = heldStore.filter((i) => sameId(i.customer_id, c.id) && i.type === "qbr" && i.date >= start && i.date <= now()).sort((a, b) => b.date.localeCompare(a.date))[0];
      const task = taskStore.filter((t) => sameId(t.customer_id, c.id) && t.type === "qbr" && t.status === "open").sort((a, b) => a.due_at.localeCompare(b.due_at))[0];
      const [status, date] = held ? ["held", held.date] : task && new Date(task.due_at) < new Date() ? ["missed", task.due_at] : task ? ["scheduled", task.due_at] : ["not_scheduled", null];
      return {
        customer: { id: c.id, code: c.code, name: c.name, segment: String(c.segment).toLowerCase(), health: c.health }, quarter: quarterLabel(), status,
        status_label: { held: "Held", missed: "Missed", scheduled: "Scheduled", not_scheduled: "Not scheduled" }[status], date, rag: held?.rag ?? null, task_id: task?.id ?? null,
        owner: staffRow(task?.owner_id ?? c.csm_id ?? c.account_manager_id),
      };
    });
};

/** A task done: a planned engagement becomes the engagement it was, once. */
const completeTask = (t, outcome = null) => {
  if (t.status === "open") Object.assign(t, { status: "done", completed_at: now() });
  let held = heldStore.find((i) => i.task_id === t.id);
  if (!held && ENGAGEMENT_KEYS.includes(t.type)) {
    held = addHeld({ customer_id: t.customer_id, type: t.type, title: t.title, summary: t.description, outcome, date: new Date(t.due_at) < new Date() ? t.due_at : now(), owner_id: t.owner_id, attendees: t.attendees, task_id: t.id });
    touched(held);
    if (t.type === "qbr" && cfg.qbr.auto_schedule && !taskStore.some((x) => x.type === "qbr" && x.status === "open" && sameId(x.customer_id, t.customer_id))) {
      addTask({ customer_id: t.customer_id, type: "qbr", title: `Quarterly business review — ${customerOf(t.customer_id)?.name}`, due_at: at10(new Date(Date.now() + cfg.qbr.every_days * DAY)), owner_id: t.owner_id, description: `Booked when the last QBR (${t.code}) was held.`, source: "system" });
    }
  }
  return held;
};
/** Any engagement moves the open renewals' last contact. */
const touched = (held) => renewalStore.filter((r) => sameId(r.customer_id, held.customer_id) && !CLOSED.includes(r.status) && (!r.last_contact_at || r.last_contact_at < held.date))
  .forEach((r) => (r.last_contact_at = held.date));

// ---- Renewals ----------------------------------------------------------------------
const RENEWAL_STATUSES = {
  upcoming: { name: "Upcoming", tone: "info" }, in_discussion: { name: "In discussion", tone: "primary" }, confirmed: { name: "Confirmed", tone: "success" },
  renewed: { name: "Renewed", tone: "success" }, at_risk: { name: "At risk", tone: "warning" }, not_renewing: { name: "Not renewing", tone: "danger" },
};
const SETTABLE = ["upcoming", "in_discussion", "at_risk"];
const CLOSED = ["renewed", "not_renewing"];
const renewalStore = sampleRenewals.map((r) => ({
  id: r.id, number: r.number, customer_id: r.customer_id, renewal_date: r.renewal_date, value: r.value, proposed_value: r.proposed_value, probability: r.probability,
  status: r.status, owner_id: r.owner_id, last_contact_at: at10(r.last_contact), note: r.note, reminders_sent: [...r.reminders_sent],
  contract: { id: 500 + r.id, code: `CON-${String(400 + r.customer_id).padStart(4, "0")}`, status: r.status === "renewed" ? "superseded" : "active" },
  renewal_contract: r.status === "renewed" ? { id: 700 + r.id, code: `CON-${String(700 + r.customer_id).padStart(4, "0")}`, status: "active" } : null,
  reason_code: null, churn_flagged_at: r.status === "not_renewing" ? ago(18) : null, confirmed_at: r.status === "confirmed" ? ago(10) : null,
  renewed_at: r.status === "renewed" ? at10(r.renewal_date) : null, lost_at: r.status === "not_renewing" ? ago(18) : null,
}));
const findRenewal = (id) => renewalStore.find((r) => sameId(r.id, id) || r.number === String(id).toUpperCase());
const renewalResource = (r) => {
  const c = customerOf(r.customer_id);
  return {
    id: r.id, number: r.number,
    customer: c ? { id: c.id, code: c.code, name: c.name, health: c.health, health_score: healthOf(c.id)?.score ?? null, lifecycle: c.lifecycle, segment: String(c.segment).toLowerCase() } : null,
    contract: { ...r.contract, end_date: r.renewal_date, auto_renew: false }, renewal_contract: r.renewal_contract, renewal_date: r.renewal_date,
    days_to_renewal: daysTo(r.renewal_date), currency: "KES", value: r.value, proposed_value: r.proposed_value ?? r.value, masked: false, probability: r.probability,
    status: r.status, status_label: RENEWAL_STATUSES[r.status]?.name ?? r.status, status_tone: RENEWAL_STATUSES[r.status]?.tone ?? "secondary", is_open: !CLOSED.includes(r.status),
    owner: staffRow(r.owner_id), last_contact_at: r.last_contact_at, note: r.note, reminders_sent: [...r.reminders_sent], reason_code: r.reason_code,
    churn_flagged_at: r.churn_flagged_at, confirmed_at: r.confirmed_at, renewed_at: r.renewed_at, lost_at: r.lost_at,
  };
};
const bucketOf = (r) => {
  const d = daysTo(r.renewal_date);
  if (d === null || d < 0) return "past";
  const marks = [...reminders()].sort((a, b) => a - b);
  const m = marks.find((x) => d <= x);
  return m === undefined ? "later" : `d${m}`;
};
const register = (all) => {
  const marks = [...reminders()].sort((a, b) => a - b);
  const open = all.filter((r) => !CLOSED.includes(r.status));
  const defs = [
    ...marks.map((m, i) => ({ key: `d${m}`, label: i === 0 ? `Due in ≤ ${m} days` : `${marks[i - 1] + 1}–${m} days`, tone: i === 0 ? "danger" : i === 1 ? "warning" : null })),
    { key: "later", label: `Over ${marks.at(-1) ?? 0} days`, tone: null },
    { key: "past", label: "Past the date", tone: "dark" },
  ];
  const buckets = defs.map((b) => {
    const inB = open.filter((r) => bucketOf(r) === b.key);
    return { ...b, count: inB.length, value: inB.reduce((s, r) => s + r.value, 0) };
  }).filter((b) => b.key !== "past" || b.count > 0);
  const forecast = Array.from({ length: 4 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + i);
    const key = d.toISOString().slice(0, 7);
    const inM = all.filter((r) => r.renewal_date?.slice(0, 7) === key && r.status !== "renewed");
    return { month: key, label: d.toLocaleString("en", { month: "short" }), up_for_renewal: inM.reduce((s, r) => s + r.value, 0), expected: Math.round(inM.reduce((s, r) => s + ((r.proposed_value ?? r.value) * r.probability) / 100, 0)) };
  });
  const soon = all.filter((r) => daysTo(r.renewal_date) >= 0 && daysTo(r.renewal_date) <= 90);
  const base = soon.reduce((s, r) => s + r.value, 0);
  return { buckets, forecast, grr: base ? Math.round((soon.reduce((s, r) => s + (r.value * r.probability) / 100, 0) / base) * 100) : null, reminders: reminders(), heuristic: true };
};
const revenueAtRisk = (all, days = 90) => {
  const rows = all.filter((r) => ["upcoming", "in_discussion", "at_risk", "confirmed", "not_renewing"].includes(r.status) && daysTo(r.renewal_date) >= 0 && daysTo(r.renewal_date) <= days);
  const flagged = rows.filter((r) => ["at_risk", "not_renewing"].includes(r.status));
  return {
    days, value: Math.round(rows.reduce((s, r) => s + (r.status === "not_renewing" ? r.value : (r.value * (100 - r.probability)) / 100), 0)), count: flagged.length,
    flagged_value: flagged.reduce((s, r) => s + r.value, 0),
    rows: [...flagged].sort((a, b) => b.value - a.value).slice(0, 8).map((r) => ({ id: r.id, number: r.number, customer: (({ id, name }) => ({ id, name }))(customerOf(r.customer_id)), value: r.value, status: r.status })),
    heuristic: true,
  };
};
const suggestedProbability = (customerId) => {
  const h = healthOf(customerId);
  const reasons = [];
  let p = h?.score ?? 70;
  reasons.push(h ? `Health score ${h.score}` : "No health score yet: 70");
  const last = lastEngaged([customerOf(customerId)])[0];
  if (last.days === null || last.days > cfg.engagement_gap_days) {
    p -= 10;
    reasons.push(`No engagement in ${cfg.engagement_gap_days} days: −10`);
  }
  const overdue = signalStore.find((s) => sameId(s.customer_id, customerId) && s.metric === "days_overdue")?.value ?? 0;
  if (overdue > 30) {
    p -= 15;
    reasons.push(`Invoice ${overdue} days overdue: −15`);
  }
  if (heldStore.some((i) => sameId(i.customer_id, customerId) && i.type === "qbr" && i.date >= quarterStart().toISOString())) {
    p += 5;
    reasons.push("QBR held this quarter: +5");
  }
  return { probability: Math.max(5, Math.min(95, Math.round(p))), reasons, heuristic: true };
};

// ---- Feedback: NPS, CSAT, the voice of the customer, feature requests ---------------
const npsGroup = (s) => (s === null || s === undefined ? null : s >= 9 ? "promoter" : s >= 7 ? "passive" : "detractor");
const npsFigures = (scores) => {
  const n = scores.length;
  if (!n) return { score: null, n: 0, promoters: 0, passives: 0, detractors: 0 };
  const p = scores.filter((s) => s >= 9).length;
  const d = scores.filter((s) => s <= 6).length;
  return { score: Math.round(((p - d) / n) * 100), n, promoters: p, passives: n - p - d, detractors: d };
};
const csatFigures = (scores) => {
  const n = scores.length;
  return {
    average: n ? Math.round((scores.reduce((s, x) => s + x, 0) / n) * 10) / 10 : null, csat_percent: n ? Math.round((scores.filter((s) => s >= 4).length / n) * 100) : null, n,
    distribution: Object.fromEntries([1, 2, 3, 4, 5].map((r) => [r, scores.filter((s) => s === r).length])),
  };
};
const npsStore = npsResponses.map((r) => ({ id: `nps-${r.id}`, customer_id: r.customer_id, contact: r.contact, score: r.score, comment: r.comment || null, date: at10(r.date) }));
const csatRows = () => [
  ...ticketFeedback.map((f) => ({ ticket_id: f.ticket_id, ticket: f.number, customer_id: f.customer_id, product_id: f.product_id, score: f.rating, comment: f.comment || null, by: f.by, via: "api", reopen: !!f.reopen, date: f.at })),
  ...csatResponses.map((r) => ({ ticket_id: null, ticket: r.ticket, customer_id: r.customer_id, product_id: customerOf(r.customer_id)?.product_ids?.[0] ?? null, score: r.score, comment: r.comment || null, by: customerOf(r.customer_id)?.primary_contact ?? null, via: "email", reopen: false, date: at10(r.date) })),
].sort((a, b) => b.date.localeCompare(a.date));

const KINDS = { praise: "Praise", complaint: "Complaint", suggestion: "Suggestion", request: "Request" };
const SENTIMENTS = { positive: { name: "Positive", tone: "success" }, neutral: { name: "Neutral", tone: "secondary" }, negative: { name: "Negative", tone: "danger" } };
const FR_STATUSES = {
  submitted: { name: "Submitted", tone: "secondary" }, under_review: { name: "Under review", tone: "info" }, planned: { name: "Planned", tone: "primary" },
  in_development: { name: "In development", tone: "purple" }, released: { name: "Released", tone: "success" }, declined: { name: "Declined", tone: "dark" },
};
const FR_MOVES = {
  submitted: ["under_review", "planned", "declined"], under_review: ["planned", "declined", "submitted"], planned: ["in_development", "under_review", "declined"],
  in_development: ["released", "planned"], released: [], declined: ["under_review"],
};
const FR_CLOSED = ["released", "declined"];

const frStore = sampleFeatureRequests.map((f) => ({
  id: `fr-${f.id}`, code: f.code, title: f.title, product_id: f.product_id, business_case: f.business_case, value_at_stake: f.value_at_stake, theme: null,
  status: slug(f.status), target: f.target, owner_id: f.owner_id, raised_on: f.raised, status_note: null, status_changed_at: null, customer_ids: [...f.customer_ids],
}));
const findFr = (id) => frStore.find((f) => sameId(f.id, id) || f.code === String(id).toUpperCase());
let frSeq = 20;

const vocStore = vocRegister.map((v) => {
  const sentiment = slug(v.sentiment);
  const theme = slug(v.theme);
  const fr = /FR-\d+/.exec(v.action ?? "")?.[0];
  return {
    id: `voc-${v.id}`, customer_id: v.customer_id, contact_name: customerOf(v.customer_id)?.primary_contact ?? null,
    kind: theme === "feature_gap" ? "request" : sentiment === "positive" ? "praise" : sentiment === "negative" ? "complaint" : "suggestion",
    source: v.source === "QBR" ? "qbr" : slug(v.source), theme, sentiment, summary: v.summary, action: v.action, owner_id: v.owner_id, received_on: v.date,
    feature_request_id: fr ? frStore.find((f) => f.code === fr)?.id ?? null : null, complaint: null, survey_response_id: null,
  };
});
let vocSeq = 50;
const vocResource = (v) => {
  const c = customerOf(v.customer_id);
  const fr = v.feature_request_id ? findFr(v.feature_request_id) : null;
  return {
    id: v.id, customer: c ? { id: c.id, code: c.code, name: c.name, health: c.health } : null, contact: v.contact_name ? { id: null, name: v.contact_name } : null,
    kind: v.kind, kind_label: KINDS[v.kind] ?? v.kind, source: v.source, source_label: v.source ? labelOf(FEEDBACK_SOURCES, v.source) : null,
    theme: v.theme, theme_label: v.theme ? labelOf(FEEDBACK_THEMES, v.theme) : null, sentiment: v.sentiment, sentiment_label: SENTIMENTS[v.sentiment]?.name ?? v.sentiment,
    sentiment_tone: SENTIMENTS[v.sentiment]?.tone ?? "secondary", summary: v.summary, action: v.action, owner: staffRow(v.owner_id), received_on: v.received_on,
    feature_request: fr ? { id: fr.id, code: fr.code, title: fr.title } : null, complaint: v.complaint, survey_response_id: v.survey_response_id,
  };
};
const frResource = (f) => ({
  id: f.id, code: f.code, title: f.title, product: productRef(f.product_id), business_case: f.business_case, value_at_stake: Number(f.value_at_stake) || 0, theme: f.theme,
  status: f.status, status_label: FR_STATUSES[f.status]?.name ?? f.status, status_tone: FR_STATUSES[f.status]?.tone ?? "secondary", moves: FR_MOVES[f.status] ?? [],
  is_open: !FR_CLOSED.includes(f.status), target: f.target, owner: staffRow(f.owner_id), raised_on: f.raised_on, status_note: f.status_note, status_changed_at: f.status_changed_at,
  customers: f.customer_ids.map((id) => customerOf(id)).filter(Boolean).map((c) => ({ id: c.id, code: c.code, name: c.name, votes: 1 })),
  votes: f.customer_ids.length, feedback_count: vocStore.filter((v) => v.feature_request_id === f.id).length,
});

// ---- Expansion ---------------------------------------------------------------------
const EXPANSION_TYPES = { additional_users: "Additional users", package_upgrade: "Package upgrade", cross_sell: "Cross-sell" };
const STAGE_OF = { Identified: "qualified", Qualified: "discovery", Proposed: "proposal", Negotiation: "negotiation", Won: "won", Lost: "lost" };
const stageRow = (key) => pipelineStages.find((s) => s.key === key) ?? { key, name: key, kind: "open", probability: 0 };
const expansionStore = sampleExpansions.map((x) => {
  const stage = STAGE_OF[x.stage] ?? "qualified";
  return {
    id: `exp-${x.id}`, code: `OPP-${String(300 + x.id).padStart(4, "0")}`, customer_id: x.customer_id, expansion_type: slug(x.type), product_id: x.product_id, package_id: x.package_id,
    description: x.description, value: x.value, stage, owner_id: x.owner_id, signal: x.signal, expected_close_on: x.expected_close,
    won_on: stage === "won" ? x.expected_close : null, closed_at: ["won", "lost"].includes(stage) ? at10(x.expected_close) : null,
  };
});
let expSeq = 50;
const findExpansion = (id) => expansionStore.find((x) => sameId(x.id, id));
const expansionProbability = (x) => {
  const stage = stageRow(x.stage).probability ?? 0;
  const health = healthOf(x.customer_id)?.score ?? 60;
  const usage = adoptionOf(x.customer_id).adoption ?? 50;
  const headroom = usage >= cfg.expansion.licence_threshold ? 100 : usage;
  return { probability: ["won", "lost"].includes(x.stage) ? stage : Math.round(0.6 * stage + 0.25 * health + 0.15 * headroom), stage, health, headroom, heuristic: true };
};
const expansionName = (x) => `${productOf(x.product_id)?.name ?? "Expansion"} — ${EXPANSION_TYPES[x.expansion_type] ?? "Expansion"} · ${customerOf(x.customer_id)?.name}`;
const expansionRow = (x) => {
  const p = expansionProbability(x);
  const s = stageRow(x.stage);
  return {
    id: x.id, code: x.code, name: expansionName(x), kind: x.expansion_type === "cross_sell" ? "cross_sell" : "upsell", expansion_type: x.expansion_type,
    expansion_label: EXPANSION_TYPES[x.expansion_type] ?? x.expansion_type, signal: x.signal, customer: customerRef(x.customer_id, ["health_score"]),
    product: productRef(x.product_id), package: packageRef(x.package_id), description: x.description, stage: x.stage, stage_name: s.name, stage_kind: s.kind,
    value: x.value, probability: p.probability, stage_probability: p.stage, expected_close_on: x.expected_close_on, owner: staffRow(x.owner_id), is_open: !x.closed_at, won_on: x.won_on,
  };
};
const suggestions = (customers) => {
  const threshold = cfg.expansion.licence_threshold;
  const sellable = products.filter(isSellable);
  const headroom = [];
  const upsell = [];
  const whitespace = [];
  const cross = [];
  customers.filter((c) => ["live", "at_risk"].includes(c.lifecycle)).forEach((c) => {
    const a = adoptionOf(c.id);
    const openHere = expansionStore.filter((x) => sameId(x.customer_id, c.id) && !x.closed_at);
    const ref = { id: c.id, code: c.code, name: c.name, health: c.health };
    if (a.adoption !== null) {
      headroom.push({ customer: ref, ratio: a.adoption, active_users: a.active_users, licensed_users: a.licensed_users });
      if (a.adoption >= threshold && !openHere.some((x) => x.expansion_type !== "cross_sell")) {
        upsell.push({ key: `licence:${c.id}`, customer: ref, expansion_type: "additional_users", signal: `Licence use at ${a.adoption}% (${a.active_users} of ${a.licensed_users})`, heuristic: true });
      }
    }
    if (cfg.expansion.healthy_only && c.health !== "healthy") return;
    const missing = sellable.filter((p) => !c.product_ids.includes(p.id));
    if (missing.length) whitespace.push({ customer: ref, missing: missing.map((p) => ({ id: p.id, code: p.code, name: p.short || p.name })) });
    missing.filter((p) => !openHere.some((x) => sameId(x.product_id, p.id))).forEach((p) => cross.push({
      key: `whitespace:${c.id}:${p.id}`, customer: ref, expansion_type: "cross_sell", product: { id: p.id, code: p.code, name: p.short || p.name }, signal: "Product not owned", heuristic: true,
    }));
  });
  return { upsell, cross_sell: cross.slice(0, 30), headroom: headroom.sort((a, b) => b.ratio - a.ratio).slice(0, 10), whitespace: whitespace.slice(0, 10), threshold };
};

// ---- Education and advocacy --------------------------------------------------------
const EDUCATION_KINDS = { training: "Training", webinar: "Webinar", guide: "Guide", product_update: "Product update" };
const ADVOCACY_KINDS = { testimonial: "Testimonial", referral: "Referral", case_study: "Case study", reference: "Reference" };
const ADVOCACY_STATUSES = { asked: "Asked", agreed: "Agreed", done: "Done", declined: "Declined" };
const day = (n) => new Date(Date.now() + n * DAY).toISOString().slice(0, 10);
const educationStore = [
  { id: "edu-1", kind: "webinar", title: "Year-end payroll and statutory changes", description: "What changes in January and how to prepare.", url: null, held_on: day(-12), product_id: 1, owner_id: 7,
    attendance: [{ id: "att-1", customer_id: 1, contact_id: null, contact_name: "Linda Mutiso", status: "attended" }, { id: "att-2", customer_id: 5, contact_id: null, contact_name: "Irene Nduta", status: "attended" }, { id: "att-3", customer_id: 3, contact_id: null, contact_name: "Amina Juma", status: "no_show" }] },
  { id: "edu-2", kind: "training", title: "Self-service leave for shop managers", description: null, url: null, held_on: day(-30), product_id: 1, owner_id: 10,
    attendance: [{ id: "att-4", customer_id: 10, contact_id: null, contact_name: "Kevin Langat", status: "attended" }] },
  { id: "edu-3", kind: "guide", title: "Reconciling M-Pesa repayments in the LMS", description: null, url: "https://help.demullalms.com/mpesa", held_on: day(-45), product_id: 2, owner_id: 7, attendance: [] },
];
const educationRow = (e) => ({
  id: e.id, kind: e.kind, kind_label: EDUCATION_KINDS[e.kind] ?? e.kind, title: e.title, description: e.description, url: e.url, held_on: e.held_on,
  product: e.product_id ? { id: e.product_id, name: productOf(e.product_id)?.short ?? productOf(e.product_id)?.name } : null, owner: staffRow(e.owner_id),
  reached: e.attendance.length, attended: e.attendance.filter((a) => a.status === "attended").length,
});
const advocacyStore = [
  { id: "adv-1", customer_id: 9, contact_name: "Philip Kiptoo", kind: "reference", status: "agreed", note: "LMS reference for other SACCOs", due_on: null, done_on: null, owner_id: 7 },
  { id: "adv-2", customer_id: 1, contact_name: "Linda Mutiso", kind: "testimonial", status: "asked", note: "Quote for the website", due_on: day(14), done_on: null, owner_id: 7 },
  { id: "adv-3", customer_id: 18, contact_name: "Hassan Ali", kind: "case_study", status: "done", note: "Logistics payroll case study", due_on: null, done_on: day(-60), owner_id: 10 },
];
let eduSeq = 10;
let advSeq = 10;
const advocacyRow = (a) => ({
  id: a.id, customer: customerRef(a.customer_id), contact_name: a.contact_name, kind: a.kind, kind_label: ADVOCACY_KINDS[a.kind] ?? a.kind, status: a.status,
  status_label: ADVOCACY_STATUSES[a.status] ?? a.status, note: a.note, due_on: a.due_on, done_on: a.done_on, owner: staffRow(a.owner_id),
});

// ---- Meta --------------------------------------------------------------------------
const CAN = { success_edit: true, engagements_create: true, engagements_edit: true, renewals_edit: true, renewals_close: true, feedback_manage: true, opportunities_create: true, surveys_send: true, configure: true };
const meta = () => ({
  customers: activeCustomers().sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({
    id: c.id, code: c.code, name: c.name, health: c.health, health_score: healthOf(c.id)?.score ?? null, lifecycle: c.lifecycle, segment: String(c.segment).toLowerCase(), csm_id: c.csm_id, account_manager_id: c.account_manager_id,
  })),
  staff: staff.filter((s) => s.status === "active").map((s) => ({ id: s.id, name: s.name, user_id: s.id, team_id: s.team_id })),
  csms: staff.filter((s) => s.status === "active" && sampleCustomers.some((c) => c.csm_id === s.id)).map((s) => ({ id: s.id, name: s.name })),
  engagement_types: ENGAGEMENT_TYPES, task_types: TASK_TYPES, priorities: priorities.map((p) => ({ value: p.key, label: p.name })),
  segments: segments.map((s) => ({ value: s.code.toLowerCase(), label: s.name })), feedback_sources: FEEDBACK_SOURCES, feedback_themes: FEEDBACK_THEMES,
  metrics: Object.entries(METRICS).map(([key, m]) => ({ key, label: m.label, type: m.type })),
  education_kinds: Object.entries(EDUCATION_KINDS).map(([key, label]) => ({ key, label })), advocacy_kinds: Object.entries(ADVOCACY_KINDS).map(([key, label]) => ({ key, label })),
  products: products.filter((p) => p.status === "active").map((p) => ({ id: p.id, code: p.code, name: p.name, short_name: p.short, is_sellable: isSellable(p) })),
  packages: packages.map((p) => ({ id: p.id, product_id: p.product_id, name: p.name, price: p.price })),
  settings: { engagement_gap_days: cfg.engagement_gap_days, renewal_reminders: reminders(), qbr: { ...cfg.qbr }, bands: { ...cfg.health_bands } },
  can: { ...CAN },
});

// ---- Customer 360's Customer Success sections ---------------------------------------
/** The health, renewals, expansion and engagement sections of one customer's 360, as Success360 gives them. */
export const successSections = (customerId) => {
  const c = customerOf(customerId);
  if (!c) return { health: { available: true, visible: false, module: "Customer Success", manual: null }, renewals: { available: true, visible: false, module: "Renewals", items: [] }, expansion: { available: true, visible: false, module: "Customer Success", items: [] }, engagement: { available: true, visible: false, module: "Customer Success" } };
  const h = healthOf(c.id);
  const items = renewalStore.filter((r) => sameId(r.customer_id, c.id)).sort((a, b) => b.renewal_date.localeCompare(a.renewal_date));
  const open = items.find((r) => !CLOSED.includes(r.status));
  const last = lastEngaged([c])[0];
  const signals = signalStore.filter((s) => sameId(s.customer_id, c.id));
  return {
    health: {
      available: true, visible: true, module: "Customer Success", heuristic: true, score: h?.score ?? null, band: h?.band ?? c.health, computed_at: h ? h.computed_at ?? ago(1) : null,
      manual: h ? null : c.health,
      indicators: h ? Object.entries(cfg.health_weights).map(([key, weight]) => ({ key, name: INDICATORS[key]?.name ?? key, weight, score: h.scores[key] ?? null, signal: INDICATORS[key]?.source ?? null })) : [],
      trend: months().map((m, i) => ({ ...m, score: h?.trend[i] ?? null, band: h?.trend[i] != null ? bandFor(h.trend[i]) : null })),
      adoption: adoptionOf(c.id), signals: { latest: signals.map(signalRow), recent: signals.map(signalRow) },
      relationship: { score: 60, strength: 67, coverage: 67, freshness: 50, contacts: 2, decision_makers: 1, gaps: ["Single decision maker"], heuristic: true },
      clv: { historic: c.arr * 2, arr: c.arr, expected_years: 3.3, expected: Math.round(c.arr * 3.3), clv: Math.round(c.arr * 5.3), heuristic: true },
      suggested_probability: suggestedProbability(c.id),
      interventions: interventionStore.filter((i) => sameId(i.customer_id, c.id)).map((i) => ({ id: i.id, rule: i.rule, action: i.action, status: i.status, created: i.created })),
      bands: { ...cfg.health_bands },
    },
    renewals: {
      available: true, visible: true, module: "Renewals", current: open ? renewalResource(open) : null, items: items.map(renewalResource),
      suggested_probability: suggestedProbability(c.id), reminders: reminders(), can: { edit: true, close: true },
    },
    expansion: {
      available: true, visible: true, module: "Customer Success", heuristic: true,
      items: expansionStore.filter((x) => sameId(x.customer_id, c.id)).map((x) => {
        const r = expansionRow(x);
        return { id: r.id, code: r.code, name: r.name, kind: r.kind, expansion_type: r.expansion_type, signal: r.signal, stage: r.stage, stage_name: r.stage_name, value: r.value, probability: r.probability, expected_close_on: r.expected_close_on, owner: r.owner, product: r.product };
      }),
      suggestions: suggestions([c]), masked: false, can: { create: true },
    },
    engagement: {
      available: true, visible: true, module: "Customer Success", last: last.last, days_since: last.days, gap_days: cfg.engagement_gap_days,
      held_90: heldStore.filter((i) => sameId(i.customer_id, c.id) && i.date >= ago(90)).length,
      recent: heldStore.filter((i) => sameId(i.customer_id, c.id)).sort((a, b) => b.date.localeCompare(a.date)).slice(0, 15).map(heldRow),
      planned: plannedFor({ customer_id: c.id }).sort((a, b) => a.due_at.localeCompare(b.due_at)).slice(0, 10).map(plannedRow),
      open_tasks: taskStore.filter((t) => t.status === "open" && sameId(t.customer_id, c.id)).length,
      qbr: qbrs().find((q) => sameId(q.customer.id, c.id)) ?? null,
      education: educationStore.flatMap((e) => e.attendance.filter((a) => sameId(a.customer_id, c.id)).map((a) => ({ title: e.title, kind: e.kind, held_on: e.held_on, contact: a.contact_name, status: a.status }))),
      advocacy: advocacyStore.filter((a) => sameId(a.customer_id, c.id)).map((a) => ({ id: a.id, kind: a.kind, status: a.status, contact_name: a.contact_name, note: a.note, done_on: a.done_on })),
      voc: vocStore.filter((v) => sameId(v.customer_id, c.id)).map((v) => ({ id: v.id, kind: v.kind, sentiment: v.sentiment, summary: v.summary, source: v.source, received_on: v.received_on })),
    },
  };
};

const required = (body, fields) => {
  const errors = Object.fromEntries(fields.filter(([key]) => body[key] === undefined || body[key] === null || String(body[key]).trim() === "").map(([key, words]) => [key, [`Give the ${words}.`]]));
  return Object.keys(errors).length ? errors : null;
};

// ---- Routes ------------------------------------------------------------------------
export default [
  ["get", "tenant/success/meta", () => ok(meta())],

  // Health
  ["get", "tenant/success/health", ({ query }) => {
    const all = healthStore.filter((h) => customerOf(h.customer_id) && customerOf(h.customer_id).lifecycle !== "former").map(healthRow);
    const q = String(query.search ?? "").trim().toLowerCase();
    const rows = all.filter((r) => (!query.band || r.band === query.band) && (!query.csm_id || sameId(r.customer.csm?.id, query.csm_id))
      && (!query.segment || r.customer.segment === query.segment) && (!q || r.customer.name.toLowerCase().includes(q)));
    const key = { arr: (r) => -(r.arr ?? 0), inactive: (r) => -(r.inactive_users ?? 0) }[query.sort] ?? ((r) => r.score ?? 999);
    rows.sort((a, b) => key(a) - key(b));
    const min = { ...cfg.health_bands, critical: 0 };
    const upper = { healthy: 100, needs_attention: cfg.health_bands.healthy - 1, at_risk: cfg.health_bands.needs_attention - 1, critical: cfg.health_bands.at_risk - 1 };
    const ms = months();
    return ok({
      rows,
      bands: Object.entries(BANDS).map(([k, name]) => ({ key: k, name, min: min[k], range: `${min[k]}–${upper[k]}`, count: all.filter((r) => r.band === k).length, arr: all.filter((r) => r.band === k).reduce((s, r) => s + r.arr, 0) })),
      indicators: Object.entries(INDICATORS).map(([k, m]) => ({ key: k, name: m.name, source: m.source, weight: cfg.health_weights[k] ?? 0 })),
      trend: ms.map((m, i) => {
        const vals = all.map((r) => r.trend[i]).filter((v) => v !== null && v !== undefined);
        return { ...m, average: vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : null };
      }),
      totals: { licensed: all.reduce((s, r) => s + (r.licensed_users ?? 0), 0), active: all.reduce((s, r) => s + (r.active_users ?? 0), 0), customers: all.length, scored: all.filter((r) => r.score !== null).length },
      interventions: interventionRows().slice(0, 25),
      masked: false, heuristic: true, can: { edit: true, configure: true },
    });
  }],
  ["post", "tenant/success/health/recalculate", ({ body }) => {
    if (body.customer_id) {
      const h = healthOf(body.customer_id);
      if (!h) return notFound("Customer not found.");
      recompute(h);
      return ok({ customer_id: h.customer_id, score: h.score, band: h.band }, `${customerOf(h.customer_id).name}: ${h.score} (${BANDS[h.band]}).`);
    }
    healthStore.forEach(recompute);
    return ok({ queued: true }, "Scores are being worked out again from the latest usage, support, engagement, payment and satisfaction data.", 202);
  }],
  ["get", "tenant/success/health/:customerId", ({ params }) => (customerOf(params.customerId) ? ok(successSections(params.customerId).health) : notFound("Customer not found."))],
  ["get", "tenant/success/interventions", () => ok({ data: interventionRows() })],
  ["post", "tenant/success/interventions/:id/complete", ({ params }) => {
    const i = interventionStore.find((x) => sameId(x.id, params.id));
    if (!i) return notFound("Intervention not found.");
    i.status = "done";
    return ok(interventionRow(i), "Marked done.");
  }],

  // Signals
  ["get", "tenant/success/signals", ({ query }) => {
    const rows = signalStore.filter((s) => (!query.customer_id || sameId(s.customer_id, query.customer_id)) && (!query.type || s.type === query.type) && (!query.metric || s.metric === query.metric))
      .sort((a, b) => b.period.localeCompare(a.period) || a.metric.localeCompare(b.metric));
    const page = paginate(rows, query, 50);
    return ok({ data: page.data.map((s) => ({ ...signalRow(s), customer: customerRef(s.customer_id) })), meta: pageMeta(page) });
  }],
  ["post", "tenant/success/signals", ({ body }) => {
    const errors = required(body, [["customer_id", "customer"], ["metric", "metric"], ["value", "value"]]);
    if (errors) return invalid(errors);
    if (!METRICS[body.metric]) return invalid({ metric: ["Choose one of the metrics."] });
    const s = { id: `sig-${signalSeq++}`, customer_id: body.customer_id, type: METRICS[body.metric].type, metric: body.metric, value: Number(body.value), period: `${body.period ?? months(1)[0].month}-01`.slice(0, 10), source: "manual", note: body.note ?? null };
    signalStore.unshift(s);
    const h = healthOf(body.customer_id);
    if (h && body.metric === "active_users") Object.assign(h, { active_users: s.value, inactive_users: Math.max(0, h.licensed_users - s.value) });
    if (h && body.metric === "licensed_users") Object.assign(h, { licensed_users: s.value, inactive_users: Math.max(0, s.value - h.active_users) });
    if (h) recompute(h);
    return created(signalRow(s), "Signal recorded; the health is being worked out again.");
  }],
  ["delete", "tenant/success/signals/:id", ({ params }) => {
    const i = signalStore.findIndex((s) => sameId(s.id, params.id));
    if (i < 0) return notFound("Signal not found.");
    signalStore.splice(i, 1);
    return ok(null, "Signal removed.");
  }],

  // Engagements, QBRs, CS tasks
  ["get", "tenant/success/engagements", ({ query }) => {
    const held = query.status === "planned" ? [] : heldFor(query).map(heldRow);
    const planned = query.status === "done" ? [] : plannedFor(query).map(plannedRow);
    const rows = [...held, ...planned].sort((a, b) => b.date.localeCompare(a.date)).slice(0, Number(query.limit) || 200);
    return ok({ data: rows, total: rows.length, stats: engagementStats(), types: ENGAGEMENT_TYPES });
  }],
  ["post", "tenant/success/engagements", ({ body }) => {
    const errors = required(body, [["customer_id", "customer"], ["type", "type"], ["title", "title"]]);
    if (errors) return invalid(errors);
    const c = customerOf(body.customer_id);
    if (!c) return notFound("Customer not found.");
    const when = body.occurred_at ? new Date(String(body.occurred_at).length <= 10 ? `${body.occurred_at}T10:00:00` : body.occurred_at) : new Date();
    if (startOfDay(when) > startOfDay(Date.now())) {
      const t = addTask({ customer_id: c.id, type: body.type, title: body.title, description: body.summary ?? null, due_at: when.toISOString(), owner_id: body.owner_id ?? c.csm_id ?? ME_STAFF, attendees: body.attendees ?? [] });
      return created(plannedRow(t), `Planned: ${t.code} is on the calendar.`);
    }
    const held = addHeld({ customer_id: c.id, type: body.type, title: body.title, summary: body.summary ?? null, outcome: body.outcome ?? null, next_step: body.next_step ?? null, rag: body.rag ?? null, date: when.toISOString(), owner_id: body.owner_id ?? ME_STAFF, attendees: body.attendees ?? [] });
    if (body.next_step && body.next_step_due) addTask({ customer_id: c.id, type: "follow_up", title: body.next_step, due_at: at10(body.next_step_due), owner_id: body.owner_id ?? ME_STAFF });
    touched(held);
    return created(heldRow(held), "Engagement logged on the customer timeline.");
  }],
  ["post", "tenant/success/engagements/:taskId/complete", ({ params, body }) => {
    const t = taskStore.find((x) => sameId(x.id, params.taskId));
    if (!t) return notFound("Planned engagement not found.");
    const held = completeTask(t, body.outcome ?? null) ?? addHeld({ customer_id: t.customer_id, type: t.type, title: t.title, date: now(), owner_id: t.owner_id, task_id: t.id });
    Object.entries({ outcome: body.outcome, summary: body.summary, next_step: body.next_step, rag: body.rag }).forEach(([k, v]) => v !== undefined && v !== null && (held[k] = v));
    return ok(heldRow(held), "Held: logged on the customer timeline.");
  }],
  ["get", "tenant/success/gone-quiet", ({ query }) => {
    const days = Number(query.days) || null;
    return ok({ data: quiet(days ?? cfg.engagement_gap_days), days: days ?? cfg.engagement_gap_days });
  }],
  ["get", "tenant/success/qbrs", () => ok({ data: qbrs() })],
  ["post", "tenant/success/qbrs", ({ body }) => {
    const errors = required(body, [["customer_id", "customer"], ["due_at", "date"]]);
    if (errors) return invalid(errors);
    const c = customerOf(body.customer_id);
    if (!c) return notFound("Customer not found.");
    const t = addTask({ customer_id: c.id, type: "qbr", title: body.title ?? `Quarterly business review — ${c.name}`, description: body.agenda ?? null, due_at: new Date(String(body.due_at).replace(" ", "T")).toISOString(), owner_id: body.owner_id ?? c.csm_id ?? c.account_manager_id, attendees: body.attendees ?? [] });
    return created(plannedRow(t), `QBR ${t.code} booked and the invitation sent.`);
  }],
  ["get", "tenant/success/tasks", ({ query }) => {
    const status = query.status || "open";
    const rows = taskStore.filter((t) => (!query.customer_id || sameId(t.customer_id, query.customer_id)) && (!query.owner_id || sameId(t.owner_id, query.owner_id)) && (!query.type || t.type === query.type))
      .filter((t) => (status === "all" ? true : status === "overdue" ? isOverdue(t) : status === "done" ? t.status === "done" : t.status === "open"))
      .sort((a, b) => a.due_at.localeCompare(b.due_at));
    const page = paginate(rows, query, 50);
    return ok({ data: page.data.map(taskRow), meta: pageMeta(page), counts: { open: taskStore.filter((t) => t.status === "open").length, overdue: taskStore.filter(isOverdue).length } });
  }],
  ["post", "tenant/success/tasks", ({ body }) => {
    const errors = required(body, [["customer_id", "customer"], ["type", "type"], ["title", "task"], ["due_at", "due date"]]);
    if (errors) return invalid(errors);
    const c = customerOf(body.customer_id);
    if (!c) return notFound("Customer not found.");
    const t = addTask({ customer_id: c.id, type: body.type, title: body.title, description: body.description ?? null, due_at: new Date(String(body.due_at).length <= 10 ? `${body.due_at}T10:00:00` : String(body.due_at).replace(" ", "T")).toISOString(), priority: body.priority || "medium", owner_id: body.owner_id || c.csm_id || ME_STAFF });
    return created({ id: t.id, code: t.code, title: t.title, due_at: t.due_at, owner: staffRow(t.owner_id) }, `Task ${t.code} created.`);
  }],
  // CS tasks are completed through the task engine's endpoint; only this area's ids are answered here.
  ["post", "tenant/tasks/cst-:n/complete", ({ params, body }) => {
    const t = taskStore.find((x) => x.id === `cst-${params.n}`);
    if (!t) return notFound("Task not found.");
    if (t.status !== "open") return fail(`${t.code} is already done.`, null, 409);
    t.completion_note = body.note ?? null;
    completeTask(t, body.note ?? null);
    return ok({ ...taskRow(t), completed_at: t.completed_at }, `${t.code} completed.`);
  }],

  // Renewals
  ["get", "tenant/renewals", ({ query }) => {
    const all = [...renewalStore].sort((a, b) => a.renewal_date.localeCompare(b.renewal_date));
    const q = String(query.search ?? "").trim().toLowerCase();
    const open = (r) => !CLOSED.includes(r.status);
    const rows = all.filter((r) => (!query.status || r.status === query.status) && (!query.owner_id || sameId(r.owner_id, query.owner_id))
      && (!query.customer_id || sameId(r.customer_id, query.customer_id)) && (!query.bucket || (open(r) && bucketOf(r) === query.bucket))
      && (!(query.open === true || query.open === "1" || query.open === "true") || open(r))
      && (!q || (customerOf(r.customer_id)?.name ?? "").toLowerCase().includes(q) || r.number.toLowerCase().includes(q)));
    return ok({
      data: rows.map(renewalResource), total: rows.length,
      counts: Object.entries(RENEWAL_STATUSES).map(([k, s]) => ({ key: k, label: s.name, tone: s.tone, count: all.filter((r) => r.status === k).length })),
      register: register(all), at_risk: revenueAtRisk(all),
      statuses: Object.entries(RENEWAL_STATUSES).map(([k, s]) => ({ key: k, name: s.name, tone: s.tone, settable: SETTABLE.includes(k) })),
      masked: false, can: { edit: true, close: true },
    });
  }],
  ["post", "tenant/renewals/sync", () => ok({ made: 0 }, "The register is up to date.")],
  ["get", "tenant/renewals/:id", ({ params }) => {
    const r = findRenewal(params.id);
    return r ? ok({ ...renewalResource(r), suggested_probability: suggestedProbability(r.customer_id), reminders: reminders() }) : notFound("Renewal not found.");
  }],
  ["put", "tenant/renewals/:id", ({ params, body }) => {
    const r = findRenewal(params.id);
    if (!r) return notFound("Renewal not found.");
    if (CLOSED.includes(r.status)) return fail(`${r.number} is ${RENEWAL_STATUSES[r.status].name.toLowerCase()}: it can no longer be changed.`, null, 409);
    if (body.status && !SETTABLE.includes(body.status)) return invalid({ status: ["Confirmed, renewed and not renewing have their own actions."] });
    if (body.probability !== undefined && (Number(body.probability) < 0 || Number(body.probability) > 100)) return invalid({ probability: ["Between 0 and 100."] });
    ["status", "note", "owner_id"].forEach((k) => body[k] !== undefined && (r[k] = body[k]));
    ["probability", "proposed_value"].forEach((k) => body[k] !== undefined && body[k] !== null && body[k] !== "" && (r[k] = Number(body[k])));
    return ok(renewalResource(r), "Saved.");
  }],
  ["post", "tenant/renewals/:id/contact", ({ params, body }) => {
    const r = findRenewal(params.id);
    if (!r) return notFound("Renewal not found.");
    const held = addHeld({ customer_id: r.customer_id, type: body.type ?? "call", title: body.title ?? `Renewal conversation (${r.number})`, summary: body.summary ?? null, outcome: body.outcome ?? null, date: body.occurred_at ? new Date(body.occurred_at).toISOString() : now(), owner_id: ME_STAFF });
    r.last_contact_at = held.date;
    if (r.status === "upcoming") r.status = "in_discussion";
    return ok(renewalResource(r), "Contact logged on the customer timeline.");
  }],
  ["post", "tenant/renewals/:id/confirm", ({ params, body }) => {
    const r = findRenewal(params.id);
    if (!r) return notFound("Renewal not found.");
    if (r.status === "confirmed") return fail(`${r.number} is already confirmed: the next term is ${r.renewal_contract?.code}.`, null, 409);
    if (CLOSED.includes(r.status)) return fail(`${r.number} is ${RENEWAL_STATUSES[r.status].name.toLowerCase()}.`, null, 409);
    const next = { id: 700 + r.id, code: `CON-${String(700 + r.customer_id).padStart(4, "0")}`, status: "pending_approval" };
    Object.assign(r, { status: "confirmed", confirmed_at: now(), probability: Math.max(95, r.probability), proposed_value: body.proposed_value ? Number(body.proposed_value) : r.proposed_value, renewal_contract: next, note: body.note ?? r.note });
    return ok(renewalResource(r), `Confirmed. The next term ${next.code} is drafted on the contract and waiting for approval.`);
  }],
  ["post", "tenant/renewals/:id/not-renewing", ({ params, body }) => {
    const r = findRenewal(params.id);
    if (!r) return notFound("Renewal not found.");
    if (!body.reason_code) return invalid({ reason_code: ["Say why the customer is not renewing: choose a churn reason."] }, "Say why the customer is not renewing: choose a churn reason.");
    if (CLOSED.includes(r.status)) return fail(`${r.number} is already ${RENEWAL_STATUSES[r.status].name.toLowerCase()}.`, null, 409);
    Object.assign(r, { status: "not_renewing", probability: 0, lost_at: now(), churn_flagged_at: now(), reason_code: body.reason_code, note: `${body.reason_code}${body.note ? ` — ${body.note}` : ""}`, renewal_contract: null });
    return ok(renewalResource(r), `Recorded as not renewing; ${customerOf(r.customer_id)?.name} is flagged as a churn risk.`);
  }],

  // Feedback
  ["get", "tenant/success/feedback", () => {
    const recent = npsStore.filter((r) => r.date >= ago(90)).sort((a, b) => a.score - b.score);
    const figures = npsFigures(recent.map((r) => r.score));
    const csat = csatRows();
    const inPeriod = csat.filter((r) => r.date >= ago(90));
    return ok({
      nps: { ...figures, total: figures.n, responses: recent.map((r) => ({ id: r.id, survey: { id: "srv-1", name: "Quarterly NPS" }, customer: customerRef(r.customer_id), contact: r.contact, score: r.score, group: npsGroup(r.score), comment: r.comment, date: r.date })) },
      csat: { ...csatFigures(inPeriod.map((r) => r.score)), in_product: inPeriod.filter((r) => r.via === "api").length, period_days: 90, responses: csat.slice(0, 60).map(({ customer_id, ...r }) => ({ ...r, customer: customerRef(customer_id), api: r.via === "api" })) },
      survey_csat: csatFigures([4, 5, 4, 3, 5]),
      trend: npsTrend.labels.map((label, i) => ({ label, nps: npsTrend.nps[i], csat: npsTrend.csat[i] })),
      voc: { total: vocStore.length, negative: vocStore.filter((v) => v.sentiment === "negative").length },
      feature_requests: { open: frStore.filter((f) => !FR_CLOSED.includes(f.status)).length, value_at_stake: frStore.filter((f) => !FR_CLOSED.includes(f.status)).reduce((s, f) => s + (Number(f.value_at_stake) || 0), 0) },
    });
  }],
  ["get", "tenant/success/feedback/meta", () => ok({
    kinds: Object.entries(KINDS).map(([key, label]) => ({ key, label })),
    sentiments: Object.entries(SENTIMENTS).map(([key, s]) => ({ key, label: s.name, tone: s.tone })),
    sources: FEEDBACK_SOURCES, themes: FEEDBACK_THEMES,
    statuses: Object.entries(FR_STATUSES).map(([key, s]) => ({ key, name: s.name, tone: s.tone, moves: FR_MOVES[key] })),
  })],
  ["get", "tenant/success/voc", ({ query }) => {
    const rows = vocStore.filter((v) => (!query.customer_id || sameId(v.customer_id, query.customer_id)) && (!query.sentiment || v.sentiment === query.sentiment)
      && (!query.kind || v.kind === query.kind) && (!query.theme || v.theme === query.theme) && matches(query.search, v.summary))
      .sort((a, b) => b.received_on.localeCompare(a.received_on));
    const sentiments = Object.fromEntries(Object.keys(SENTIMENTS).map((k) => [k, rows.filter((v) => v.sentiment === k).length]).filter(([, n]) => n));
    return ok({ ...resourcePage(rows.map(vocResource), query), sentiments });
  }],
  ["post", "tenant/success/voc", ({ body }) => {
    const errors = required(body, [["customer_id", "customer"], ["summary", "summary"]]);
    if (errors) return invalid(errors);
    const v = { id: `voc-${vocSeq++}`, customer_id: body.customer_id, contact_name: body.contact_name ?? null, kind: body.kind ?? "suggestion", source: body.source ?? null, theme: body.theme ?? null,
      sentiment: body.sentiment ?? "neutral", summary: body.summary, action: body.action ?? null, owner_id: body.owner_id ?? ME_STAFF, received_on: body.received_on ?? day(0),
      feature_request_id: body.feature_request_id ?? null, complaint: null, survey_response_id: null };
    if (body.create_feature_request && !v.feature_request_id) {
      const fr = { id: `fr-${frSeq}`, code: `FR-0${frSeq++}`, title: body.feature_title ?? String(v.summary).slice(0, 120), product_id: body.product_id ?? null, business_case: body.business_case ?? v.summary,
        value_at_stake: Number(body.value_at_stake) || 0, theme: v.theme, status: "submitted", target: null, owner_id: ME_STAFF, raised_on: day(0), status_note: null, status_changed_at: null, customer_ids: [v.customer_id] };
      frStore.unshift(fr);
      v.feature_request_id = fr.id;
      v.action ??= `Feature request ${fr.code}`;
    }
    vocStore.unshift(v);
    return created(vocResource(v), "Feedback recorded.");
  }],
  ["put", "tenant/success/voc/:id", ({ params, body }) => {
    const v = vocStore.find((x) => sameId(x.id, params.id));
    if (!v) return notFound("Feedback not found.");
    ["kind", "source", "theme", "sentiment", "summary", "action", "received_on", "owner_id", "feature_request_id"].forEach((k) => body[k] !== undefined && (v[k] = body[k]));
    return ok(vocResource(v), "Saved.");
  }],
  ["get", "tenant/feature-requests", ({ query }) => {
    const statuses = [].concat(query.status ?? []).flatMap((s) => String(s).split(",")).filter(Boolean);
    const rows = frStore.filter((f) => (!statuses.length || statuses.includes(f.status)) && (!query.product_id || sameId(f.product_id, query.product_id))
      && (!query.customer_id || f.customer_ids.some((id) => sameId(id, query.customer_id))) && matches(query.search, f.title, f.code))
      .sort((a, b) => Number(FR_CLOSED.includes(a.status)) - Number(FR_CLOSED.includes(b.status)) || (Number(b.value_at_stake) || 0) - (Number(a.value_at_stake) || 0));
    return ok({
      ...resourcePage(rows.map(frResource), query),
      counts: Object.entries(FR_STATUSES).map(([key, s]) => ({ key, label: s.name, count: frStore.filter((f) => f.status === key).length })),
      statuses: Object.entries(FR_STATUSES).map(([key, s]) => ({ key, name: s.name, tone: s.tone, moves: FR_MOVES[key] })),
    });
  }],
  ["get", "tenant/feature-requests/:id", ({ params }) => (findFr(params.id) ? ok(frResource(findFr(params.id))) : notFound("Feature request not found."))],
  ["post", "tenant/feature-requests", ({ body }) => {
    const errors = required(body, [["title", "request"], ["business_case", "business case"]]);
    if (errors) return invalid(errors);
    const fr = { id: `fr-${frSeq}`, code: `FR-0${frSeq++}`, title: body.title, product_id: body.product_id ?? null, business_case: body.business_case, value_at_stake: Number(body.value_at_stake) || 0,
      theme: body.theme ?? null, status: "submitted", target: body.target ?? null, owner_id: body.owner_id ?? ME_STAFF, raised_on: day(0), status_note: null, status_changed_at: null,
      customer_ids: [...new Set((body.customer_ids ?? []).filter((id) => customerOf(id)).map((id) => customerOf(id).id))] };
    frStore.unshift(fr);
    return created(frResource(fr), `${fr.code} submitted.`);
  }],
  ["put", "tenant/feature-requests/:id", ({ params, body }) => {
    const fr = findFr(params.id);
    if (!fr) return notFound("Feature request not found.");
    ["title", "product_id", "business_case", "theme", "target", "owner_id"].forEach((k) => body[k] !== undefined && (fr[k] = body[k]));
    if (body.value_at_stake !== undefined) fr.value_at_stake = Number(body.value_at_stake) || 0;
    (body.customer_ids ?? []).forEach((id) => customerOf(id) && !fr.customer_ids.some((x) => sameId(x, id)) && fr.customer_ids.push(customerOf(id).id));
    return ok(frResource(fr), "Saved.");
  }],
  ["post", "tenant/feature-requests/:id/status", ({ params, body }) => {
    const fr = findFr(params.id);
    if (!fr) return notFound("Feature request not found.");
    if (fr.status === body.to) return ok(frResource(fr), `${fr.code} is now ${FR_STATUSES[fr.status].name}.`);
    if (!(FR_MOVES[fr.status] ?? []).includes(body.to)) {
      const allowed = (FR_MOVES[fr.status] ?? []).map((s) => FR_STATUSES[s].name).join(", ") || "nowhere";
      return fail(`${fr.code} is ${FR_STATUSES[fr.status].name}: it can move to ${allowed}.`, null, 409);
    }
    if (body.to === "declined" && !String(body.note ?? "").trim()) return invalid({ note: ["Say why it is declined; the customers asking will be told."] }, "Say why it is declined; the customers asking will be told.");
    Object.assign(fr, { status: body.to, status_note: body.note ?? null, status_changed_at: now(), target: body.target ?? fr.target });
    return ok(frResource(fr), `${fr.code} is now ${FR_STATUSES[fr.status].name}.`);
  }],
  ["post", "tenant/feature-requests/:id/votes", ({ params, body }) => {
    const fr = findFr(params.id);
    const c = customerOf(body.customer_id);
    if (!fr || !c) return notFound();
    if (!fr.customer_ids.some((id) => sameId(id, c.id))) fr.customer_ids.push(c.id);
    return ok(frResource(fr), `${c.name} added.`);
  }],
  ["delete", "tenant/feature-requests/:id/votes/:customerId", ({ params }) => {
    const fr = findFr(params.id);
    if (!fr) return notFound();
    fr.customer_ids = fr.customer_ids.filter((id) => !sameId(id, params.customerId));
    return ok(frResource(fr), "Removed.");
  }],

  // Expansion (3.5 opportunities of kind upsell / cross_sell)
  ["get", "tenant/success/expansion", ({ query }) => {
    const all = expansionStore.map(expansionRow);
    const rows = all.filter((r) => (!query.kind || r.kind === query.kind) && (!query.stage || r.stage === query.stage));
    const open = all.filter((r) => r.is_open);
    const qs = quarterStart().toISOString().slice(0, 10);
    return ok({
      data: rows,
      totals: { open: open.reduce((s, r) => s + r.value, 0), open_count: open.length, weighted: Math.round(open.reduce((s, r) => s + (r.value * r.probability) / 100, 0)),
        won_quarter: all.filter((r) => r.stage === "won" && r.won_on >= qs).reduce((s, r) => s + r.value, 0), nrr: 108 },
      stages: pipelineStages.map((s) => ({ key: s.key, name: s.name, kind: s.kind, probability: s.probability })),
      types: Object.entries(EXPANSION_TYPES).map(([key, label]) => ({ key, label })),
      suggestions: suggestions(activeCustomers()), masked: false, heuristic: true, can: { create: true, edit: true, close: true },
    });
  }],
  ["post", "tenant/success/expansion", ({ body }) => {
    const errors = required(body, [["customer_id", "customer"], ["value", "value"]]);
    if (errors) return invalid(errors);
    if (!(Number(body.value) > 0)) return invalid({ value: ["The value must be greater than zero."] });
    const c = customerOf(body.customer_id);
    if (!c) return notFound("Customer not found.");
    const x = { id: `exp-${expSeq}`, code: `OPP-${String(300 + expSeq++).padStart(4, "0")}`, customer_id: c.id, expansion_type: body.expansion_type || "cross_sell", product_id: body.product_id ?? null,
      package_id: body.package_id ?? null, description: body.description ?? null, value: Number(body.value), stage: "qualified", owner_id: body.owner_id ?? c.account_manager_id,
      signal: body.signal || "Raised by Customer Success", expected_close_on: body.expected_close_on || day(45), won_on: null, closed_at: null };
    expansionStore.unshift(x);
    return created({ id: x.id, code: x.code, name: expansionName(x), kind: x.expansion_type === "cross_sell" ? "cross_sell" : "upsell", stage: x.stage, owner: staffRow(x.owner_id) },
      `${x.code} opened and given to ${staffRow(x.owner_id)?.name ?? "the account manager"}.`);
  }],
  // The pipeline's own endpoints, for this area's expansion ids only.
  ["post", "tenant/records/opportunity/exp-:n/stage", ({ params, body }) => {
    const x = findExpansion(`exp-${params.n}`);
    if (!x) return notFound("Opportunity not found.");
    if (x.closed_at) return fail(`${x.code} is closed; revive it to work it again.`, null, 409);
    const to = pipelineStages.find((s) => s.key === body.to);
    if (!to) return invalid({ to: ["Choose one of the pipeline's stages."] });
    if (to.kind !== "open") return invalid({ to: ["Won and lost have their own actions."] });
    const from = x.stage;
    x.stage = to.key;
    return ok({ stage: x.stage, history: { from, to: x.stage, at: now(), by: "Jane Wanjiku" }, ageing: { stage: x.stage, days: 0 } }, `Moved to ${to.name}.`);
  }],
  ["post", "tenant/opportunities/exp-:n/win", ({ params }) => {
    const x = findExpansion(`exp-${params.n}`);
    if (!x) return notFound("Opportunity not found.");
    if (x.closed_at) return fail(`${x.code} is already closed.`, null, 409);
    Object.assign(x, { stage: "won", won_on: day(0), closed_at: now() });
    return ok(expansionRow(x), `${x.code} won. An amendment to ${customerOf(x.customer_id)?.name}'s subscription goes for approval.`);
  }],
  ["post", "tenant/opportunities/exp-:n/lose", ({ params, body }) => {
    const x = findExpansion(`exp-${params.n}`);
    if (!x) return notFound("Opportunity not found.");
    if (!body.reason_code) return invalid({ reason_code: ["Choose why it was lost."] }, "Choose why it was lost.");
    if (x.closed_at) return fail(`${x.code} is already closed.`, null, 409);
    Object.assign(x, { stage: "lost", closed_at: now() });
    return ok(expansionRow(x), `${x.code} marked lost.`);
  }],

  // Education and advocacy
  ["get", "tenant/success/education", ({ query }) => {
    const rows = educationStore.filter((e) => !query.kind || e.kind === query.kind).sort((a, b) => String(b.held_on).localeCompare(String(a.held_on)));
    const page = paginate(rows, query, 50);
    return ok({ data: page.data.map(educationRow), meta: pageMeta(page) });
  }],
  ["get", "tenant/success/education/:id", ({ params }) => {
    const e = educationStore.find((x) => sameId(x.id, params.id));
    return e ? ok({ ...educationRow(e), attendance: e.attendance.map((a) => ({ id: a.id, customer: customerRef(a.customer_id), contact_id: a.contact_id, contact_name: a.contact_name, status: a.status })) }) : notFound("Education item not found.");
  }],
  ["post", "tenant/success/education", ({ body }) => {
    const errors = required(body, [["kind", "kind"], ["title", "title"]]);
    if (errors) return invalid(errors);
    const e = { id: `edu-${eduSeq++}`, kind: body.kind, title: body.title, description: body.description ?? null, url: body.url ?? null, held_on: body.held_on ?? null, product_id: body.product_id ?? null, owner_id: body.owner_id ?? ME_STAFF, attendance: [] };
    educationStore.unshift(e);
    return created(educationRow(e), "Saved.");
  }],
  ["put", "tenant/success/education/:id", ({ params, body }) => {
    const e = educationStore.find((x) => sameId(x.id, params.id));
    if (!e) return notFound("Education item not found.");
    ["kind", "title", "description", "url", "held_on", "product_id", "owner_id"].forEach((k) => body[k] !== undefined && (e[k] = body[k]));
    return ok(educationRow(e), "Saved.");
  }],
  ["post", "tenant/success/education/:id/attendance", ({ params, body }) => {
    const e = educationStore.find((x) => sameId(x.id, params.id));
    if (!e) return notFound("Education item not found.");
    e.attendance = (body.attendance ?? []).map((a, i) => ({ id: `att-${params.id}-${i}`, customer_id: a.customer_id, contact_id: a.contact_id ?? null, contact_name: a.contact_name ?? null, status: a.status ?? "attended" }));
    return ok({ ...educationRow(e), attendance: e.attendance.map((a) => ({ ...a, customer: customerRef(a.customer_id) })) });
  }],
  ["get", "tenant/success/advocacy", () => ok({
    data: advocacyStore.map(advocacyRow),
    suggestions: npsStore.filter((r) => r.score >= 9 && !advocacyStore.some((a) => sameId(a.customer_id, r.customer_id))).map((r) => ({ customer: customerRef(r.customer_id), contact_name: r.contact, reason: `NPS ${r.score}`, score: r.score })),
  })],
  ["post", "tenant/success/advocacy", ({ body }) => {
    const errors = required(body, [["customer_id", "customer"], ["kind", "kind"]]);
    if (errors) return invalid(errors);
    const a = { id: `adv-${advSeq++}`, customer_id: body.customer_id, contact_name: body.contact_name ?? null, kind: body.kind, status: body.status ?? "asked", note: body.note ?? null, due_on: body.due_on ?? null, done_on: body.done_on ?? null, owner_id: body.owner_id ?? ME_STAFF };
    advocacyStore.unshift(a);
    return created(advocacyRow(a), "Saved.");
  }],
  ["put", "tenant/success/advocacy/:id", ({ params, body }) => {
    const a = advocacyStore.find((x) => sameId(x.id, params.id));
    if (!a) return notFound("Advocacy item not found.");
    ["contact_name", "kind", "status", "note", "due_on", "done_on", "owner_id"].forEach((k) => body[k] !== undefined && (a[k] = body[k]));
    return ok(advocacyRow(a), "Saved.");
  }],

  // Settings → Customer Success Rules
  ["get", "tenant/settings/customer-success", () => ok({
    settings: structuredClone(cfg),
    indicators: Object.entries(INDICATORS).map(([key, m]) => ({ key, label: m.name, source: m.source })),
    bands: Object.entries(BANDS).map(([key, label]) => ({ key, label })),
    segments: segments.map((s) => ({ value: s.code.toLowerCase(), label: s.name })),
    survey_triggers: [],
    samples: [...healthStore].sort((a, b) => a.score - b.score).slice(0, 6).map((h) => ({ id: h.customer_id, name: customerOf(h.customer_id)?.name, scores: { ...h.scores } })),
    can: { configure: true },
  })],
  ["put", "tenant/settings/customer-success", ({ body }) => {
    if (!String(body.reason ?? "").trim()) return invalid({ reason: ["Say why the rules change."] }, "Say why the rules change.");
    const { reason, effective_date, ...rules } = body;
    if (rules.health_weights) {
      const total = Object.values(rules.health_weights).reduce((s, v) => s + Number(v || 0), 0);
      if (total !== 100) return invalid({ health_weights: [`The weights add up to ${total}; they must add up to 100.`] });
    }
    if (rules.churn_scoring && Object.values(rules.churn_scoring.weights ?? {}).reduce((s, v) => s + Number(v || 0), 0) !== 100) {
      return invalid({ churn_scoring: ["Give each part of the churn-risk score a weight; together they must add up to 100%."] }, "Give each part of the churn-risk score a weight; together they must add up to 100%.");
    }
    Object.entries(rules).forEach(([k, v]) => k in cfg && (cfg[k] = structuredClone(v)));
    return ok({ settings: structuredClone(cfg) }, "Customer Success rules saved.");
  }],
];
